import { AfterViewInit, Directive, ElementRef, OnDestroy, ViewChild, inject } from '@angular/core';
import { Router } from '@angular/router';
import { RequestService } from '@df/api/request/request.service';
import { BasketTotalsUpdatingService } from '@df/basket/view/basket-totals-updating.service';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { Events } from '@df/core/service/event';
import { Status } from '@df/core/status';
import { Stripe } from '@df/module-stripe/stripe.namespace';
import { StripeService } from '@df/module-stripe/stripe.service';
import { Basket } from '@df/session/api/basket/basket';
import { UiComponent } from '@df/ui/ui.component';
import uniq from 'lodash-es/uniq';

@Directive()
// tslint:disable-next-line: directive-class-suffix
export abstract class StripeExpressPaymentRequestButtonComponentDef extends UiComponent implements AfterViewInit, OnDestroy {
    private router = inject(Router);
    private events = inject(Events);
    private stripeService = inject(StripeService);
    private basket = inject(Basket);
    private request = inject(RequestService);
    private basketTotalsUpdatingService = inject(BasketTotalsUpdatingService);
    /**
     * GP on/off
     * @type {boolean}
     */
    protected useGooglePay = true;

    /**
     * AP on/off
     * @type {boolean}
     */
    protected useApplePay = true;

    /**
     * Browser cards on/off
     * @type {boolean}
     */
    protected useBrowserCard = false;

    /**
     * Can make payment result
     * @type {Stripe.IPaymentRequestInstanceCanMakePaymentResult}
     */
    protected canMakePaymentResult?: Stripe.IPaymentRequestInstanceCanMakePaymentResult;

    @ViewChild('container')
    private container?: ElementRef<any>;

    override canDetach = false;

    status = new Status();

    createPaymentRequestButtonStatus = new Status();

    totalsUpdatingClickStatus = new Status();

    private paymentRequestButtonElementInstance?: Stripe.IPaymentRequestButtonElementInstance;

    private elements?: Stripe.IElementsInstance;

    private shippingAddress?: Stripe.IShippingAddress;

    private stripe?: Stripe.Instance;

    private lastUpdateRequiredData?: string;

    private onBasketUpdateDebounced = this.timeoutService.debounce(() => this.onBasketUpdate(), 1000);

    ngAfterViewInit(): void {
        if (this.canDisplayElements()) {
            this.init();
        }
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.clearPaymentRequestButtonElementInstance();
    }

    private createPaymentRequestButton(): void {
        this.request.send(
            this.createPaymentRequestButtonStatus,
            () => {
                return new Promise((resolve, reject) => {
                    const stripe = this.stripe;
                    const elements = this.elements;
                    const container = this.container?.nativeElement;
                    if (stripe && elements && container) {
                        this.lastUpdateRequiredData = JSON.stringify(this.getUpdateRequiredData());
                        this.stripeService.getPaymentRequestOptions(
                            undefined,
                            { type: 'cart' },
                            (paymentRequestOptions: Stripe.IPaymentRequestOptions) => {
                                const options = paymentRequestOptions && this.getPaymentRequestOptions(paymentRequestOptions);
                                if (options) {
                                    const paymentRequestInstance = stripe.paymentRequest(options);
                                    paymentRequestInstance
                                        .canMakePayment()
                                        .then((result: Stripe.IPaymentRequestInstanceCanMakePaymentResult) => {
                                            if (result) {
                                                this.canMakePaymentResult = result;
                                                resolve({ data: paymentRequestInstance });
                                            } else {
                                                reject();
                                            }
                                        })
                                        .catch(reject);
                                } else {
                                    reject();
                                }
                            }
                        );
                    } else {
                        reject();
                    }
                });
            },
            (paymentRequestInstance: Stripe.IPaymentRequestInstance | undefined) => {
                this.onPaymentRequestInstanceUpdate(paymentRequestInstance);
            },
            () => {
                this.onPaymentRequestInstanceUpdate();
            }
        );
    }

    private onPaymentRequestInstanceUpdate(paymentRequestInstance?: Stripe.IPaymentRequestInstance): void {
        if (!this.totalsUpdating) {
            this.totalsUpdatingClickStatus.reset();
        }

        this.clearPaymentRequestButtonElementInstance();
        if (paymentRequestInstance) {
            paymentRequestInstance.on(
                Stripe.PaymentRequestEvent.shippingaddresschange,
                (event: Stripe.IPaymentRequestShippingAddressChangeEvent) => this.onShippingAddressChange(event)
            );
            paymentRequestInstance.on(
                Stripe.PaymentRequestEvent.shippingoptionchange,
                (event: Stripe.IPaymentRequestShippingOptionChangeEvent) => this.onShippingOptionChange(event)
            );

            if (!this.elements) {
                throw new Error('Element is not set');
            }
            const paymentRequestButton = this.elements.create(Stripe.Component.PaymentRequestButton, {
                paymentRequest: paymentRequestInstance,
                style: { paymentRequestButton: this.stripeService.getPaymentRequestButtonConfig()!.buttonConfig }
            });
            this.paymentRequestButtonElementInstance = paymentRequestButton;
            paymentRequestButton.on(Stripe.ElementEvent.Ready, () => {
                this.onPaymentRequestButtonReadyEvent(paymentRequestButton, paymentRequestInstance);
            });
            if (!this.container) {
                throw new Error('Container is not set');
            }
            paymentRequestButton.mount(this.container.nativeElement);
        }
        this.detectChanges();
    }

    private getPaymentRequestOptions(paymentRequestOptions: Stripe.IPaymentRequestOptions): Stripe.IPaymentRequestOptions | undefined {
        if (paymentRequestOptions) {
            const disabledWallets = this.getDisabledWallets(paymentRequestOptions.disableWallets);
            if (disabledWallets.length !== 3) {
                paymentRequestOptions.disableWallets = disabledWallets;
                return paymentRequestOptions;
            }
        }
        return undefined;
    }

    /**
     * Returns disabled wallets, disabled by flag in component, not BE config
     * @param {Stripe.IPaymentRequestWallet[]} disabledWallets
     * @returns {Stripe.IPaymentRequestWallet[]}
     */
    private getDisabledWallets(disabledWallets?: Stripe.IPaymentRequestWallet[]): Stripe.IPaymentRequestWallet[] {
        disabledWallets = disabledWallets || [];
        if (!this.useBrowserCard) {
            disabledWallets.push('browserCard');
        }
        if (!this.useGooglePay) {
            disabledWallets.push('googlePay');
        }
        if (!this.useApplePay || !this.basket.data.stripe?.isApplePayEnabled) {
            disabledWallets.push('applePay');
        }
        return uniq(disabledWallets);
    }

    private init(): void {
        this.stripeService.loadScript(() => {
            const config = this.stripeService.getPaymentRequestButtonConfig();
            if (config) {
                const locale = config.initParams.locale;

                const key = config.initParams.apiKey;
                if (!key) {
                    throw new Error('Stripe API key is not set');
                }

                this.stripe = this.stripeService.getStripe(key, { locale });
                this.elements = this.stripe!.elements({ locale });
                this.onElementsCreated();
            }
        });
    }

    private onElementsCreated(): void {
        this.createPaymentRequestButton();
        this.subscriptions.add(this.basket.subscribe(() => this.onBasketUpdateDebounced()));
        [this.totalsUpdatingClickStatus, this.createPaymentRequestButtonStatus].forEach(i =>
            this.subscriptions.add(i.subscribe(() => this.detectChanges()))
        );
        this.subscriptions.add(
            this.basketTotalsUpdatingService.subject.subscribe(() => {
                if (!this.totalsUpdating) {
                    this.totalsUpdatingClickStatus.reset();
                }
            })
        );
    }

    private get totalsUpdating(): boolean {
        return this.basketTotalsUpdatingService.totalsUpdating;
    }

    private onBasketUpdate(): void {
        if (this.isUpdateRequired()) {
            this.createPaymentRequestButton();
        }
    }

    private isUpdateRequired(): boolean {
        return JSON.stringify(this.getUpdateRequiredData()) !== this.lastUpdateRequiredData;
    }

    private getUpdateRequiredData(): Stripe.IPaymentRequestUpdateRequiredData {
        return {
            total: this.basket.data?.totals?.grand_total,
            items: this.basket.items.map(basketItem => {
                return {
                    amount: basketItem.rowPrice(),
                    label: basketItem.product?.data?.name,
                    id: basketItem.data?.id
                };
            })
        };
    }

    private clearPaymentRequestButtonElementInstance(): void {
        if (this.paymentRequestButtonElementInstance) {
            try {
                this.paymentRequestButtonElementInstance.clear();
                this.paymentRequestButtonElementInstance.destroy();
                this.paymentRequestButtonElementInstance.unmount();
                this.paymentRequestButtonElementInstance = undefined;
            } catch (e) {
                this.paymentRequestButtonElementInstance = undefined;
            }
        }
    }

    private canDisplayElements(): boolean {
        return !!this.container?.nativeElement;
    }

    protected onPaymentRequestButtonClickEvent(event: Stripe.IElementInstaceClickEvent): void {
        this.totalsUpdatingClickStatus.reset();

        if (!this.basket.isSalable || this.totalsUpdating || this.createPaymentRequestButtonStatus.busy || this.status.busy) {
            event.preventDefault();
            if (!this.basket.isSalable) {
                this.status.error = 'Some of your items are no longer available, please remove them to continue';
            } else if (this.totalsUpdating || this.createPaymentRequestButtonStatus.busy) {
                this.totalsUpdatingClickStatus.error = 'Totals are currently updating';
            }
        }
    }

    /**
     * On payment request button element instance ready event handler
     * @param {Stripe.IPaymentRequestButtonElementInstance} paymentRequstButton
     * @param {Stripe.IPaymentRequestInstance}             paymentRequest
     */
    private onPaymentRequestButtonReadyEvent(
        paymentRequstButton: Stripe.IPaymentRequestButtonElementInstance,
        paymentRequest: Stripe.IPaymentRequestInstance
    ): void {
        paymentRequstButton.on(Stripe.ElementEvent.Click, (event: Stripe.IElementInstaceClickEvent) =>
            this.onPaymentRequestButtonClickEvent(event)
        );
        paymentRequest.on(Stripe.PaymentRequestEvent.paymentmethod, (event: Stripe.IPaymentRequestResponse) =>
            this.onPaymentRequestPaymentMethodEvent(event)
        );
    }

    /**
     * while shipping address is set or changed, we call BE for shipping method list and update the list
     * inside external payment provider modal
     * @param {Stripe.IPaymentRequestShippingAddressChangeEvent} event
     */
    onShippingAddressChange(event: Stripe.IPaymentRequestShippingAddressChangeEvent): void {
        this.shippingAddress = this.convertAddress(event.shippingAddress);
        const address = this.shippingAddress;
        this.stripeService.expressEstimateCart(
            this.status,
            { address },
            (estimateCartData: Stripe.IExpressEstimateCartResult) => {
                if (estimateCartData.results.length < 1) {
                } else {
                    this.stripeService.expressApplyShipping(
                        this.status,
                        { address, shipping_id: estimateCartData.results[0].id! },
                        result => {
                            const updateData = {
                                status: Stripe.PaymentRequestCompleteStatus.success,
                                shippingOptions: estimateCartData.results,
                                ...result.results
                            };
                            event.updateWith(updateData);
                        },
                        () => this.onError(event)
                    );
                }
            },
            () => this.onError(event)
        );
    }

    /**
     * Called while user chnages shipping method in external payment provider modal,
     * choice is saved to BE and updated within external payment provider modal
     * @param {Stripe.IPaymentRequestShippingOptionChangeEvent} event
     */
    onShippingOptionChange(event: Stripe.IPaymentRequestShippingOptionChangeEvent): void {
        this.stripeService.expressApplyShipping(
            this.status,
            { address: this.shippingAddress!, shipping_id: event.shippingOption.id! },
            (result: Stripe.IExpressApplyShippingResult) => {
                const updateData = { status: Stripe.PaymentRequestCompleteStatus.success, ...result.results };
                event.updateWith(<Stripe.IPaymentRequestUpdateDetails>updateData);
            },
            () => this.onError(event)
        );
    }

    /**
     * Called on payment confirmation, we place the actual order here.
     * @param {Stripe.IPaymentRequestResponse} event
     */
    onPaymentRequestPaymentMethodEvent(event: Stripe.IPaymentRequestResponse): void {
        const { complete, ...data } = event;
        const basketData = this.basket.data;
        this.stripeService.expressPlaceOrder(
            this.status,
            { ...data, location: 'cart' },
            (result: Checkout.ISubmissionResponse) => {
                complete(Stripe.PaymentRequestCompleteStatus.success);
                const eventDdata: Checkout.ISubmitSuccessEventData = {
                    request: undefined,
                    basket: basketData,
                    response: result
                };
                this.events.broadcast<Checkout.ISubmitSuccessEventData>(Checkout.EVENTS.SubmitSuccess, eventDdata);
                if (!result.post_redirect && !result.external_redirect && !result.preventSuccess) {
                    this.status.superBusy = true;
                    this.router.navigate(['/checkout/success']);
                }
            },
            () => complete(Stripe.PaymentRequestCompleteStatus.fail)
        );
    }

    /**
     * Address conversion method, moved from native magento module
     * @param {Stripe.IShippingAddress} address
     * @returns {Stripe IShippingAddress}
     */
    convertAddress(address: Stripe.IShippingAddress): Stripe.IShippingAddress {
        if (address.city.length == 0 && address.region.length > 0) {
            address.city = address.region;
        }
        return address;
    }

    /**
     * Passes error into events
     * @param {Stripe.IPaymentRequestShippingOptionChangeEvent | Stripe.IPaymentRequestShippingAddressChangeEvent} event
     */
    onError(event: Stripe.IPaymentRequestShippingOptionChangeEvent | Stripe.IPaymentRequestShippingAddressChangeEvent): void {
        event.updateWith({ status: Stripe.PaymentRequestCompleteStatus.fail });
    }
}
